import React, { useEffect, useMemo, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  getPublicClubApi,
  getPublicMatchesApi,
} from '../api/match.api';

import { toJalaliFull } from '../utils/jalali';

import Card from '../components/common/Card';
import Icon from '../components/common/Icon';
import PitchView from '../components/common/PitchView';
import StatusBadge from '../components/common/StatusBadge';

import { MATCH_STATUS_LABELS } from '../utils/constants';

const POSITION_LABELS = {
  GOALKEEPER: 'دروازه‌ بان',
  GK: 'دروازه‌ بان',

  DEFENDER: 'مدافع',
  DEFENCE: 'مدافع',
  CB: 'مدافع میانی',
  LB: 'مدافع چپ',
  RB: 'مدافع راست',
  LWB: 'وینگ‌ بک چپ',
  RWB: 'وینگ‌ بک راست',

  MIDFIELDER: 'هافبک',
  MIDFIELD: 'هافبک',
  CM: 'هافبک میانی',
  CDM: 'هافبک دفاعی',
  CAM: 'هافبک هجومی',
  LM: 'هافبک چپ',
  RM: 'هافبک راست',

  FORWARD: 'مهاجم',
  ATTACKER: 'مهاجم',
  ST: 'مهاجم',
  CF: 'مهاجم نوک',
  LW: 'وینگر چپ',
  RW: 'وینگر راست',
};

const STAFF_ROLE_LABELS = {
  HEAD_COACH: 'سرمربی',
  ASSISTANT_COACH: 'مربی',
  TECHNICAL_MANAGER: 'مدیر فنی',
  FITNESS_COACH: 'مربی بدنساز',
  GOALKEEPER_COACH: 'مربی دروازه ‌بانان',
  DOCTOR: 'پزشک تیم',
  PHYSIOTHERAPIST: 'فیزیوتراپیست',
  MANAGER: 'مدیر باشگاه',
};

function getImageUrl(path) {
  if (!path || typeof path !== 'string') return null;

  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('blob:')
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || '';

  if (!base) return path;

  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

function getPlayerName(player) {
  if (!player) return 'بازیکن';

  return (
    player.fullName ||
    player.name ||
    [player.firstName, player.lastName].filter(Boolean).join(' ') ||
    'بازیکن'
  );
}

function getPlayerPosition(player) {
  const position =
    player?.mainPosition ||
    player?.position ||
    player?.secondPosition;

  return POSITION_LABELS[position] || position || 'بازیکن';
}

function getStaffName(member) {
  return (
    member?.name ||
    member?.fullName ||
    [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
    'عضو کادر'
  );
}

function getStaffRole(member) {
  return (
    STAFF_ROLE_LABELS[member?.role] ||
    member?.position ||
    member?.title ||
    'عضو کادر فنی'
  );
}

function getStatusBadge(status) {
  const map = {
    SCHEDULED: 'pending',
    UPCOMING: 'pending',
    LIVE: 'active',
    PLAYED: 'active',
    FINISHED: 'inactive',
    COMPLETED: 'inactive',
    CANCELED: 'inactive',
    POSTPONED: 'pending',
  };

  return (
    <StatusBadge
      status={map[status] || 'inactive'}
      label={MATCH_STATUS_LABELS[status] || status || 'نامشخص'}
    />
  );
}

function extractItems(response) {
  const value = response?.data?.data;

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.matches)) return value.matches;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.matches)) return response.data.matches;

  return [];
}

function toFaNumber(value) {
  return Number(value || 0).toLocaleString('fa-IR');
}

export default function ClubDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [club, setClub] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);

  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    async function loadClub() {
      setLoading(true);
      setError('');
      setClub(null);

      try {
        const response = await getPublicClubApi(id);

        if (!mounted) return;

        const clubData =
          response?.data?.data ||
          response?.data ||
          null;

        setClub(clubData);
      } catch (err) {
        if (!mounted) return;

        setClub(null);

        setError(
          err?.response?.data?.message ||
            'دریافت اطلاعات باشگاه با خطا مواجه شد.'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (id) {
      loadClub();
    }

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!club?.id) {
      setRecentMatches([]);
      return;
    }

    let mounted = true;

    async function loadMatches() {
      setMatchesLoading(true);

      try {
        const response = await getPublicMatchesApi({
          clubId: club.id,
          limit: 6,
          page: 1,
        });

        if (!mounted) return;

        const items = extractItems(response);

        const sortedItems = [...items].sort((a, b) => {
          const dateA = new Date(
            a?.date ||
              a?.matchDate ||
              a?.startAt ||
              a?.scheduledAt ||
              0
          ).getTime();

          const dateB = new Date(
            b?.date ||
              b?.matchDate ||
              b?.startAt ||
              b?.scheduledAt ||
              0
          ).getTime();

          return dateB - dateA;
        });

        setRecentMatches(sortedItems.slice(0, 6));
      } catch {
        if (mounted) setRecentMatches([]);
      } finally {
        if (mounted) setMatchesLoading(false);
      }
    }

    loadMatches();

    return () => {
      mounted = false;
    };
  }, [club?.id]);

  const players = useMemo(() => {
    if (Array.isArray(club?.players)) return club.players;
    if (Array.isArray(club?.players?.items)) return club.players.items;
    if (Array.isArray(club?.players?.data)) return club.players.data;
    return [];
  }, [club]);

  const staff = useMemo(() => {
    if (Array.isArray(club?.staff)) return club.staff;
    if (Array.isArray(club?.staff?.items)) return club.staff.items;
    if (Array.isArray(club?.staff?.data)) return club.staff.data;
    return [];
  }, [club]);

  const achievements = useMemo(() => {
    if (Array.isArray(club?.achievements)) return club.achievements;
    if (Array.isArray(club?.achievements?.items)) {
      return club.achievements.items;
    }
    if (Array.isArray(club?.achievements?.data)) {
      return club.achievements.data;
    }
    return [];
  }, [club]);

  const pitchPlayers = useMemo(() => {
    return players.map((player) => ({
      ...player,

      photo:
        player.photo ||
        player.avatar ||
        player.image ||
        player.profileImage ||
        null,

      mainPosition:
        player.mainPosition ||
        player.position ||
        player.secondPosition ||
        'MIDFIELDER',

      secondPosition:
        player.secondPosition ||
        player.mainPosition ||
        player.position ||
        null,

      shirtNumber:
        player.shirtNumber ||
        player.number ||
        player.jerseyNumber ||
        null,

      fullName: getPlayerName(player),

      club: {
        ...(player.club || {}),
        id: club?.id,
        name: club?.name,
        logo: club?.logo,
      },
    }));
  }, [players, club]);

  const stats = useMemo(() => {
    return {
      players:
        players.length ||
        club?._count?.players ||
        club?.playerCount ||
        0,

      matches:
        club?._count?.matches ||
        club?._count?.homeMatches ||
        club?.matchesCount ||
        recentMatches.length ||
        0,

      wins: club?.wins || club?.stats?.wins || 0,

      achievements:
        achievements.length ||
        club?._count?.achievements ||
        0,
    };
  }, [club, players.length, achievements.length, recentMatches.length]);

  const logo = getImageUrl(
    club?.logo ||
      club?.logoUrl ||
      club?.image
  );

  if (loading) {
    return (
      <main className="min-h-screen">
        <div className="container-page py-24">
          <div className="mx-auto flex max-w-lg flex-col items-center justify-center text-center">
            <div className="relative">
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />

              <div className="absolute inset-0 grid place-items-center">
                <Icon
                  name="shield"
                  className="h-5 w-5 text-emerald-500"
                />
              </div>
            </div>

            <p className="mt-5 text-sm font-bold text-slate-400">
              در حال دریافت اطلاعات باشگاه...
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (!club) {
    return (
      <main className="min-h-screen">
        <div className="container-page py-20">
          <Card
            hover={false}
            className="mx-auto max-w-xl rounded-[2rem] p-8 text-center sm:p-10"
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-red-50 text-red-500">
              <Icon
                name="alert-circle"
                className="h-8 w-8"
              />
            </div>

            <h1 className="mt-5 text-xl font-black text-[#0B1F17]">
              باشگاه پیدا نشد
            </h1>

            <p className="mt-2 text-sm leading-7 text-slate-400">
              {error || 'اطلاعات این باشگاه در دسترس نیست.'}
            </p>

            <button
              type="button"
              onClick={() => navigate('/clubs')}
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-l from-emerald-600 to-emerald-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5"
            >
              <Icon
                name="arrow-right"
                className="h-4 w-4"
              />
              بازگشت به باشگاه‌ها
            </button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen overflow-hidden pb-20">
      {/* =====================================================
          CLUB HERO
      ===================================================== */}
      <section className="hero-panel relative isolate mx-3 overflow-hidden rounded-[2.25rem] bg-[#07150F] shadow-[0_50px_100px_-40px_rgba(7,21,15,0.7)] sm:mx-5 sm:rounded-[3rem]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-bl from-[#07150F] via-[#0A2619] to-[#05100B]" />

          <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-emerald-500/10 blur-3xl" />

          <div className="pointer-events-none absolute -bottom-52 -left-40 h-[500px] w-[500px] rounded-full bg-lime-500/10 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        </div>

        <div className="container-page relative z-10 py-7 sm:py-10 lg:py-14">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate('/clubs')}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-white/55 backdrop-blur-xl transition hover:border-emerald-400/30 hover:bg-white/[0.08] hover:text-white"
            >
              <Icon
                name="arrow-right"
                className="h-4 w-4"
              />
              همه باشگاه‌ها
            </button>

            <div className="hidden items-center gap-2 text-[11px] text-white/40 sm:flex">
              <Link
                to="/"
                className="transition hover:text-emerald-300"
              >
                خانه
              </Link>

              <span>/</span>

              <Link
                to="/clubs"
                className="transition hover:text-emerald-300"
              >
                باشگاه‌ها
              </Link>

              <span>/</span>

              <span className="max-w-[180px] truncate text-white/70">
                {club.name}
              </span>
            </div>
          </div>

          <div className="mt-10 grid items-center gap-8 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
            {/* Logo */}
            <div className="relative mx-auto lg:mx-0">
              <div className="absolute -inset-5 rounded-[2.5rem] bg-emerald-400/10 blur-2xl" />

              <div className="relative grid h-32 w-32 place-items-center overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.08] shadow-2xl shadow-black/30 backdrop-blur-xl sm:h-36 sm:w-36">
                {logo ? (
                  <img
                    src={logo}
                    alt={club.name}
                    className="h-full w-full object-contain p-5"
                  />
                ) : (
                  <span className="text-5xl font-black text-emerald-300">
                    {club.name?.charAt(0) || '⚽'}
                  </span>
                )}
              </div>

              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-emerald-400/20 bg-[#0C2A1D] px-3 py-1 text-[9px] font-black text-emerald-300 shadow-lg">
                باشگاه رسمی
              </div>
            </div>

            {/* Information */}
            <div className="min-w-0 text-center lg:text-right">
              <div className="flex flex-wrap justify-center gap-2 lg:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5 text-[10px] font-black text-emerald-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  عضو سامانه فوتبال
                </span>

                {club.status === 'ACTIVE' && (
                  <span className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[10px] font-black text-white/60">
                    فعال
                  </span>
                )}
              </div>

              <h1 className="mt-5 text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
                {club.name}
              </h1>

              {club.nickname && (
                <p className="mt-2 text-sm font-bold text-white/40">
                  {club.nickname}
                </p>
              )}

              {club.slogan && (
                <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-2xl border border-emerald-400/10 bg-emerald-400/[0.06] px-4 py-2 text-xs font-bold text-emerald-200">
                  <Icon
                    name="quote"
                    className="h-4 w-4 shrink-0 text-emerald-400"
                  />
                  <span className="truncate">
                    {club.slogan}
                  </span>
                </div>
              )}

              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-3 text-xs text-white/50 lg:justify-start">
                {club.city && (
                  <span className="inline-flex items-center gap-2">
                    <Icon
                      name="map-pin"
                      className="h-4 w-4 text-emerald-400"
                    />
                    {club.city}
                  </span>
                )}

                {club.establishmentDate && (
                  <span className="inline-flex items-center gap-2">
                    <Icon
                      name="calendar"
                      className="h-4 w-4 text-lime-300"
                    />
                    تأسیس{' '}
                    {toJalaliFull(club.establishmentDate)}
                  </span>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
              <HeroStat
                icon="users"
                value={toFaNumber(stats.players)}
                label="بازیکن"
              />

              <HeroStat
                icon="trophy"
                value={toFaNumber(stats.matches)}
                label="مسابقه"
              />

              <HeroStat
                icon="award"
                value={toFaNumber(stats.wins)}
                label="برد"
              />

              <HeroStat
                icon="medal"
                value={toFaNumber(stats.achievements)}
                label="افتخار"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="container-page relative z-20 -mt-5">
        {/* =====================================================
            ABOUT + CONTACT
        ===================================================== */}
        <section className="grid gap-6 lg:grid-cols-[1.35fr_.65fr]">
          <Card
            hover={false}
            className="rounded-[2rem] border-slate-200 p-6 sm:p-7"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-emerald-50 to-lime-50 text-emerald-600">
                <Icon
                  name="shield"
                  className="h-5 w-5"
                />
              </div>

              <div>
                <span className="text-[10px] font-black text-emerald-600">
                  معرفی رسمی
                </span>

                <h2 className="mt-0.5 text-lg font-black text-[#0B1F17]">
                  درباره باشگاه {club.name}
                </h2>
              </div>
            </div>

            <p className="mt-6 whitespace-pre-line text-sm leading-8 text-slate-600">
              {club.description ||
                'توضیحات رسمی برای این باشگاه هنوز ثبت نشده است.'}
            </p>

            {(club.city || club.address || club.establishmentDate) && (
              <div className="mt-7 grid gap-3 border-t border-slate-100 pt-5 sm:grid-cols-2">
                {club.city && (
                  <MiniInfo
                    icon="map-pin"
                    label="شهر"
                    value={club.city}
                  />
                )}

                {club.establishmentDate && (
                  <MiniInfo
                    icon="calendar"
                    label="تاریخ تأسیس"
                    value={toJalaliFull(club.establishmentDate)}
                  />
                )}
              </div>
            )}
          </Card>

          <Card
            hover={false}
            className="rounded-[2rem] border-slate-200 p-6 sm:p-7"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-900 text-white">
                <Icon
                  name="phone"
                  className="h-5 w-5"
                />
              </div>

              <div>
                <span className="text-[10px] font-black text-slate-400">
                  ارتباط مستقیم
                </span>

                <h2 className="mt-0.5 text-lg font-black text-[#0B1F17]">
                  اطلاعات تماس
                </h2>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {club.phone && (
                <InfoRow
                  icon="phone"
                  label="شماره تماس"
                  value={club.phone}
                />
              )}

              {club.email && (
                <InfoRow
                  icon="mail"
                  label="پست الکترونیک"
                  value={club.email}
                />
              )}

              {club.address && (
                <InfoRow
                  icon="map-pin"
                  label="آدرس باشگاه"
                  value={club.address}
                />
              )}

              {!club.phone &&
                !club.email &&
                !club.address && (
                  <EmptyText text="اطلاعات تماس برای این باشگاه ثبت نشده است." />
                )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {club.websiteUrl && (
                <SocialLink
                  href={club.websiteUrl}
                  icon="globe"
                  label="وب‌سایت"
                />
              )}

              {club.instagramUrl && (
                <SocialLink
                  href={club.instagramUrl}
                  icon="instagram"
                  label="اینستاگرام"
                />
              )}

              {club.telegramUrl && (
                <SocialLink
                  href={club.telegramUrl}
                  icon="telegram"
                  label="تلگرام"
                />
              )}
            </div>
          </Card>
        </section>

        {/* =====================================================
            PITCH
        ===================================================== */}
        <section className="mt-12">
          <SectionTitle
            eyebrow="ترکیب تاکتیکی"
            title={`بازیکنان ${club.name} در زمین`}
            subtitle="بازیکنان براساس پست اصلی و جایگاه تاکتیکی آن‌ ها روی زمین فوتبال نمایش داده می‌ شوند."
            count={`${toFaNumber(players.length)} بازیکن`}
          />

          {pitchPlayers.length === 0 ? (
            <Card
              hover={false}
              className="rounded-[2rem]"
            >
              <EmptyState
                icon="users"
                title="بازیکنی برای نمایش وجود ندارد"
                description="به‌ محض ثبت بازیکنان باشگاه، ترکیب آن‌ ها روی زمین نمایش داده خواهد شد."
              />
            </Card>
          ) : (
            <>
              <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/5 sm:p-4">
                <div className="absolute left-0 top-0 h-24 w-24 rounded-full bg-emerald-400/10 blur-3xl" />

                <div className="relative">
                  <PitchView
                    players={pitchPlayers}
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 text-center text-[11px] text-slate-400">
                <Icon
                  name="info"
                  className="h-3.5 w-3.5 text-emerald-500"
                />
                روی تصویر یا آواتار هر بازیکن کلیک کنید تا مشخصات او نمایش داده شود.
              </div>
            </>
          )}
        </section>

        {/* =====================================================
            PLAYERS
        ===================================================== */}
        {players.length > 0 && (
          <section className="mt-12">
            <SectionTitle
              eyebrow="فهرست بازیکنان"
              title="بازیکنان باشگاه"
              subtitle="مشاهده سریع اعضای تیم و ورود به پروفایل کامل هر بازیکن."
              count={`${toFaNumber(players.length)} بازیکن`}
            />

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {players.map((player) => (
                <PlayerListCard
                  key={player.id}
                  player={player}
                />
              ))}
            </div>
          </section>
        )}

        {/* =====================================================
            MATCHES
        ===================================================== */}
        <section className="mt-12">
          <SectionTitle
            eyebrow="مرکز مسابقات"
            title="آخرین مسابقات باشگاه"
            subtitle="جدیدترین بازی‌ ها و نتایج ثبت‌ شده این باشگاه."
            action={
              <Link
                to="/competitions"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
              >
                همه مسابقات
                <Icon
                  name="arrow-left"
                  className="h-3.5 w-3.5"
                />
              </Link>
            }
          />

          {matchesLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-44 animate-pulse rounded-[1.75rem] bg-white"
                />
              ))}
            </div>
          ) : recentMatches.length === 0 ? (
            <Card
              hover={false}
              className="rounded-[2rem]"
            >
              <EmptyState
                icon="trophy"
                title="مسابقه‌ای ثبت نشده است"
                description="در حال حاضر مسابقه‌ای برای نمایش در پروفایل این باشگاه وجود ندارد."
              />
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recentMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                />
              ))}
            </div>
          )}
        </section>

        {/* =====================================================
            STAFF
        ===================================================== */}
        {staff.length > 0 && (
          <section className="mt-12">
            <SectionTitle
              eyebrow="کادر باشگاه"
              title="کادر فنی و اجرایی"
              subtitle="اعضای رسمی کادر فنی و اجرایی باشگاه."
              count={`${toFaNumber(staff.length)} عضو`}
            />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {staff.map((member) => (
                <StaffCard
                  key={member.id}
                  member={member}
                />
              ))}
            </div>
          </section>
        )}

        {/* =====================================================
            ACHIEVEMENTS
        ===================================================== */}
        {achievements.length > 0 && (
          <section className="mt-12">
            <SectionTitle
              eyebrow="تالار افتخارات"
              title="افتخارات باشگاه"
              subtitle="دستاوردها و افتخارات ثبت‌ شده باشگاه."
              count={`${toFaNumber(achievements.length)} افتخار`}
            />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {achievements.map((achievement, index) => (
                <AchievementCard
                  key={achievement.id || index}
                  achievement={achievement}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

function HeroStat({ icon, value, label }) {
  return (
    <div className="group rounded-2xl border border-white/10 bg-white/[0.055] px-3 py-3 text-center backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:bg-white/[0.09]">
      <div className="mx-auto grid h-8 w-8 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300">
        <Icon
          name={icon}
          className="h-4 w-4"
        />
      </div>

      <div className="mt-2 text-lg font-black text-white">
        {value}
      </div>

      <div className="mt-0.5 text-[9px] font-bold text-white/40">
        {label}
      </div>
    </div>
  );
}

function SectionTitle({
  eyebrow,
  title,
  subtitle,
  count,
  action,
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <div className="mb-2 inline-flex items-center gap-2 text-[10px] font-black text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {eyebrow}
          </div>
        )}

        <h2 className="text-2xl font-black tracking-tight text-[#0B1F17] sm:text-3xl">
          {title}
        </h2>

        {subtitle && (
          <p className="mt-2 max-w-2xl text-xs leading-6 text-slate-400 sm:text-sm">
            {subtitle}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {count && (
          <span className="w-fit rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-[10px] font-black text-emerald-600">
            {count}
          </span>
        )}

        {action}
      </div>
    </div>
  );
}

function PlayerListCard({ player }) {
  const photo = getImageUrl(
    player.photo ||
      player.avatar ||
      player.image ||
      player.profileImage
  );

  const name = getPlayerName(player);
  const position = getPlayerPosition(player);

  const shirtNumber =
    player.shirtNumber ||
    player.number ||
    player.jerseyNumber;

  return (
    <Link
      to={`/players/${player.id}`}
      className="group relative flex min-w-0 items-center gap-4 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-900/10"
    >
      <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-500/[0.04] transition duration-500 group-hover:scale-[2]" />

      <div className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-br from-emerald-50 to-slate-100">
        {photo ? (
          <img
            src={photo}
            alt={name}
            className="h-full w-full object-cover"
          />
        ) : (
          <Icon
            name="user"
            className="h-6 w-6 text-emerald-500"
          />
        )}
      </div>

      <div className="relative min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-black text-slate-800 transition group-hover:text-emerald-600">
            {name}
          </h3>

          {shirtNumber && (
            <span className="grid h-6 min-w-6 place-items-center rounded-lg bg-slate-900 px-1 text-[9px] font-black text-white">
              {shirtNumber}
            </span>
          )}
        </div>

        <div className="mt-2 flex items-center gap-2 text-[10px] font-bold text-slate-400">
          <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-600">
            {position}
          </span>

          {player.secondPosition && (
            <span className="truncate">
              {POSITION_LABELS[player.secondPosition] ||
                player.secondPosition}
            </span>
          )}
        </div>
      </div>

      <div className="relative grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-300 transition group-hover:bg-emerald-50 group-hover:text-emerald-500">
        <Icon
          name="arrow-left"
          className="h-4 w-4"
        />
      </div>
    </Link>
  );
}

function StaffCard({ member }) {
  const name = getStaffName(member);

  const photo = getImageUrl(
    member.photo ||
      member.avatar ||
      member.image ||
      member.profileImage
  );

  return (
    <Card
      hover
      className="rounded-[1.5rem] p-5"
    >
      <div className="flex items-center gap-4">
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-lime-50 text-lg font-black text-emerald-600">
          {photo ? (
            <img
              src={photo}
              alt={name}
              className="h-full w-full object-cover"
            />
          ) : (
            name.charAt(0)
          )}
        </div>

        <div className="min-w-0">
          <h3 className="truncate text-sm font-black text-slate-800">
            {name}
          </h3>

          <p className="mt-1 text-xs font-bold text-emerald-600">
            {getStaffRole(member)}
          </p>

          {member.phone && (
            <p className="mt-2 text-[10px] text-slate-400">
              {member.phone}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

function AchievementCard({ achievement }) {
  return (
    <Card
      hover
      className="relative overflow-hidden rounded-[1.75rem] p-5"
    >
      <div className="absolute -left-8 -top-8 h-24 w-24 rounded-full bg-amber-400/10" />

      <div className="relative flex gap-4">
        <div className="grid h-13 w-13 h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 text-xl">
          🏆
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-black leading-6 text-slate-800">
            {achievement.title ||
              achievement.name ||
              'افتخار باشگاه'}
          </h3>

          {achievement.year && (
            <span className="mt-2 inline-flex rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-black text-amber-600">
              سال {achievement.year}
            </span>
          )}

          {achievement.description && (
            <p className="mt-3 text-xs leading-6 text-slate-500">
              {achievement.description}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

function MiniInfo({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-emerald-500 shadow-sm">
        <Icon
          name={icon}
          className="h-4 w-4"
        />
      </div>

      <div className="min-w-0">
        <p className="text-[9px] font-bold text-slate-400">
          {label}
        </p>

        <p className="mt-1 truncate text-xs font-black text-slate-700">
          {value}
        </p>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-emerald-500 shadow-sm">
        <Icon
          name={icon}
          className="h-4 w-4"
        />
      </span>

      <div className="min-w-0">
        <p className="text-[10px] font-bold text-slate-400">
          {label}
        </p>

        <p className="mt-1 break-words text-xs font-bold leading-6 text-slate-700">
          {value}
        </p>
      </div>
    </div>
  );
}

function SocialLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-black text-slate-600 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
    >
      <Icon
        name={icon}
        className="h-3.5 w-3.5"
      />
      {label}
    </a>
  );
}

function EmptyText({ text }) {
  return (
    <p className="py-8 text-center text-sm leading-7 text-slate-400">
      {text}
    </p>
  );
}

function EmptyState({
  icon,
  title,
  description,
}) {
  return (
    <div className="py-10 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-50 text-slate-300">
        <Icon
          name={icon}
          className="h-6 w-6"
        />
      </div>

      <h3 className="mt-4 text-sm font-black text-slate-700">
        {title}
      </h3>

      <p className="mx-auto mt-2 max-w-md text-xs leading-7 text-slate-400">
        {description}
      </p>
    </div>
  );
}

function MatchCard({ match }) {
  const homeClub =
    match.homeClub ||
    match.homeTeam ||
    match.teamA ||
    match.clubA ||
    null;

  const awayClub =
    match.awayClub ||
    match.awayTeam ||
    match.teamB ||
    match.clubB ||
    null;

  const home =
    homeClub?.name ||
    homeClub?.club?.name ||
    'میزبان';

  const away =
    awayClub?.name ||
    awayClub?.club?.name ||
    'مهمان';

  const homeLogo = getImageUrl(
    homeClub?.logo ||
      homeClub?.club?.logo
  );

  const awayLogo = getImageUrl(
    awayClub?.logo ||
      awayClub?.club?.logo
  );

  const homeScore =
    match.result?.homeScore ??
    match.homeScore ??
    match.score?.home;

  const awayScore =
    match.result?.awayScore ??
    match.awayScore ??
    match.score?.away;

  const played =
    ['PLAYED', 'FINISHED', 'COMPLETED'].includes(match.status);

  const date =
    match.date ||
    match.matchDate ||
    match.startAt ||
    match.scheduledAt;

  return (
    <Card
      hover
      className="relative overflow-hidden rounded-[1.75rem] p-5"
    >
      <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-500/[0.04]" />

      <div className="relative flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-slate-400">
          {date
            ? toJalaliFull(date)
            : 'تاریخ نامشخص'}
        </span>

        {getStatusBadge(match.status)}
      </div>

      <div className="relative mt-7 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <TeamMini
          name={home}
          logo={homeLogo}
          align="right"
        />

        <div
          dir="ltr"
          className="grid min-h-12 min-w-[62px] place-items-center rounded-2xl bg-[#0B1F17] px-2 text-xs font-black text-white shadow-lg shadow-slate-900/10"
        >
          {played
            ? `${awayScore ?? 0} - ${homeScore ?? 0}`
            : 'VS'}
        </div>

        <TeamMini
          name={away}
          logo={awayLogo}
          align="left"
        />
      </div>

      {(match.venue ||
        match.location ||
        match.stadium) && (
        <div className="relative mt-6 flex items-center gap-2 border-t border-slate-100 pt-4 text-[10px] text-slate-400">
          <Icon
            name="map-pin"
            className="h-3.5 w-3.5 shrink-0 text-emerald-500"
          />

          <span className="truncate">
            {match.venue ||
              match.location ||
              match.stadium}
          </span>
        </div>
      )}
    </Card>
  );
}

function TeamMini({ name, logo, align }) {
  return (
    <div
      className={`min-w-0 ${
        align === 'left'
          ? 'text-left'
          : 'text-right'
      }`}
    >
      <div
        className={`flex items-center gap-2 ${
          align === 'left'
            ? 'justify-start'
            : 'justify-end'
        }`}
      >
        {align === 'left' && (
          <TeamLogo logo={logo} name={name} />
        )}

        <span className="truncate text-xs font-black text-slate-700">
          {name}
        </span>

        {align !== 'left' && (
          <TeamLogo logo={logo} name={name} />
        )}
      </div>
    </div>
  );
}

function TeamLogo({ logo, name }) {
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
      {logo ? (
        <img
          src={logo}
          alt={name}
          className="h-full w-full object-contain p-1"
        />
      ) : (
        <Icon
          name="shield"
          className="h-4 w-4 text-slate-300"
        />
      )}
    </div>
  );
}